import { useQuery } from '@tanstack/react-query';
import type { PublicDonationPageDto } from '@signalvote/shared';
import { API_BASE_URL } from '../../config';
import { queryKeys } from '../../lib/queryKeys';

/**
 * The public donation page is unauthenticated: a BARE fetch with NO Authorization,
 * NO X-Campaign-Id. The donor-facing path is neutral (`/api/donations/{slug}`, no
 * subsidiary segment); the subsidiary rides the `X-Subsidiary` header the
 * per-subsidiary origin's build supplies (ADR-052). A 404 (missing/unpublished slug,
 * or unknown subsidiary surfaced by the backend) resolves to a `{ notFound: true }`
 * sentinel the page renders as "not available" — a normal state here, not a query error.
 */
export interface PublicDonationNotFound {
  notFound: true;
}

export type PublicDonationPageResult = PublicDonationPageDto | PublicDonationNotFound;

export function isPublicDonationNotFound(
  result: PublicDonationPageResult | undefined,
): result is PublicDonationNotFound {
  return !!result && 'notFound' in result && result.notFound === true;
}

export function usePublicDonationPage(subsidiary: string, slug: string) {
  return useQuery<PublicDonationPageResult>({
    queryKey: queryKeys.publicDonation.page(subsidiary, slug),
    queryFn: async () => {
      const response = await fetch(
        `${API_BASE_URL}/api/donations/${slug}`,
        { headers: { 'X-Subsidiary': subsidiary } },
      );
      if (response.status === 404) {
        return { notFound: true };
      }
      if (!response.ok) {
        throw new Error('Failed to load the donation page');
      }
      return (await response.json()) as PublicDonationPageDto;
    },
    enabled: true,
    staleTime: 30_000,
  });
}
